"use client";

import React from "react";
import { Button } from "../../../../components/ui/button";
import {
  Compass,
  Sprout,
  Mountain,
  Lightbulb,
  Globe,
  Flame,
  ArrowRight,
} from "lucide-react";
import { useRouter } from "next/navigation";

const Page = () => {
  const router = useRouter();
  const handleClick = () => {
    router.push("/trails");
  };
  return (
    <section className="bg-white text-[#0D0D0D] py-10 px-6 sm:px-10 lg:px-24 mx-auto ">
      <div className="space-y-10">
        <div className="text-center space-y-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Compass className="w-8 h-8 text-[#FFAE00]" />
            <h1 className="text-4xl sm:text-5xl font-bricolage font-medium text-[#FFAE00]">
              Our Story
            </h1>
          </div>
        </div>

        <div className="border-l-4 border-[#FFAE00] pl-6 mb-8">
          <h2 className="text-2xl sm:text-3xl text-[#0D0D0D] font-medium font-bricolage mb-4">
            Where Purpose Meets the Path
          </h2>

          <p className="text-lg leading-relaxed font-jakarta mb-4">
            What if travel wasn&apos;t just about where you&apos;re going — but{" "}
            <em>who you become</em> along the way?
          </p>

          <p className="text-lg leading-relaxed font-jakarta mb-4">
            At Outcome Outing, we&apos;re not building just another travel
            company. We&apos;re building transformational journeys — where the
            right people, thoughtful settings, and curated experiences come
            together to create breakthroughs.
          </p>

          <p className="text-lg leading-relaxed font-jakarta">
            We believe in bonfire conversations that linger, mountain hikes that
            give perspective, and moments of clarity that come when you&apos;re
            surrounded by the <em>right tribe</em>, not just a tour group.
          </p>
        </div>

        <div className="border-l-4 border-[#FFAE00] pl-6">
          <div className="flex items-center gap-3 mb-4">
            <Sprout className="w-6 h-6 text-[#FFAE00]" />
            <h2 className="text-2xl sm:text-3xl text-[#FFAE00] font-medium font-bricolage">
              How It All Began
            </h2>
          </div>

          <p className="text-lg leading-relaxed font-jakarta mb-4">
            The seed was planted on Indian roads and rooftop cafés. It started
            as a question between two founders —{" "}
            <em>
              &quot;What if we could turn a trip into a catalyst for
              clarity?&quot;
            </em>
          </p>

          <p className="text-lg leading-relaxed font-jakarta mb-4">
            Having hosted travelers for years, we saw the power of India as a
            land of stories. But it wasn&apos;t the places alone — it was the
            energy of the people who walked through them. Founders in between
            pivots. Creators seeking silence. Students searching for direction.
            Every journey had its own pulse — we just had to listen.
          </p>

          <p className="text-lg leading-relaxed font-jakarta">
            From that realization, <strong>Outcome Outing</strong> was born — a
            space where travel meets growth, where mentors walk beside you, and
            where community feels like home.
          </p>
        </div>

        <div className="border-l-4 border-[#FFAE00] pl-6">
          <div className="flex items-center gap-3 mb-4">
            <Mountain className="w-6 h-6 text-[#FFAE00]" />
            <h2 className="text-2xl sm:text-3xl text-[#FFAE00] font-medium font-bricolage">
              A Pause That Resets Everything
            </h2>
          </div>

          <p className="text-lg leading-relaxed font-jakarta mb-4">
            There&apos;s something magical about leaving your usual world
            behind.
          </p>

          <p className="text-lg leading-relaxed font-jakarta mb-4">
            Whenever we travel, even for a few days, life slows down. The noise
            fades. Deadlines disappear. For a while, you&apos;re not thinking
            about everything that&apos;s pending — you&apos;re just{" "}
            <em>here</em>. In the moment. With people you&apos;ve never met
            before, yet feel deeply connected to.
          </p>

          <p className="text-lg leading-relaxed font-jakarta mb-4">
            We realized these moments of detachment are perfect portals for
            reflection, learning, and personal breakthroughs.
          </p>

          <p className="text-lg leading-relaxed font-jakarta mb-4">
            What if, in those few days of freedom, you could reset your mindset?
            What if clarity could be curated?
          </p>

          <p className="text-lg leading-relaxed font-jakarta">
            That&apos;s why Outcome Outing exists — not to distract you from
            life, but to help you return to it with{" "}
            <strong>renewed focus</strong>, <strong>direction</strong>, and a{" "}
            <strong>bigger story to tell</strong>.
          </p>
        </div>

        <div className="border-l-4 border-[#FFAE00] pl-6">
          <div className="flex items-center gap-3 mb-4">
            <Lightbulb className="w-6 h-6 text-[#FFAE00]" />
            <h2 className="text-2xl sm:text-3xl text-[#FFAE00] font-medium font-bricolage">
              Beyond Sightseeing
            </h2>
          </div>

          <p className="text-lg leading-relaxed font-jakarta mb-4">
            We don&apos;t do generic.
          </p>

          <p className="text-lg leading-relaxed font-jakarta mb-4">We do:</p>

          <ul className="list-disc list-inside text-lg font-jakarta space-y-3 pl-4 mb-4">
            <li>
              Domain-based curated groups (entrepreneurs, creators, coders,
              photographers, students)
            </li>
            <li>
              Expert-led sessions in villas, cafes, and valleys — not classrooms
            </li>
            <li>
              Personal takeaways: content, clarity, connections, confidence
            </li>
            <li>Real-world impact, not just resort check-ins</li>
          </ul>

          <p className="text-lg leading-relaxed font-jakarta">
            Our focus? <strong>Learning through experience.</strong> Every trip
            is an <em>Outcome Sprint</em> — with just the right balance of
            downtime, reflection, and fire-starting action.
          </p>
        </div>

        <div className="border-l-4 border-[#FFAE00] pl-6">
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-6 h-6 text-[#FFAE00]" />
            <h2 className="text-2xl sm:text-3xl text-[#FFAE00] font-medium font-bricolage">
              Who We Curate For
            </h2>
          </div>

          <p className="text-lg leading-relaxed font-jakarta mb-4">
            We&apos;re not for everyone. But if you&apos;re someone who…
          </p>

          <ul className="list-disc list-inside text-lg font-jakarta space-y-3 pl-4 mb-4">
            <li>Craves deep conversations over shallow itineraries</li>
            <li>Wants to break patterns, not just take pictures</li>
            <li>Feels ready for something more than &quot;a getaway&quot;</li>
          </ul>

          <p className="text-lg leading-relaxed font-jakarta">
            …you&apos;ll fit right in.
          </p>

          <p className="text-lg leading-relaxed font-jakarta mt-4">
            We welcome misfits, makers, marketers, students, solopreneurs, and
            seekers — anyone bold enough to show up open-hearted, and leave with
            more than they arrived with.
          </p>
        </div>

        <div className="border-l-4 border-[#FFAE00] pl-6">
          <div className="flex items-center gap-3 mb-4">
            <Flame className="w-6 h-6 text-[#FFAE00]" />
            <h2 className="text-2xl sm:text-3xl text-[#FFAE00] font-medium font-bricolage">
              The Journey Ahead
            </h2>
          </div>

          <p className="text-lg leading-relaxed font-jakarta mb-4">
            Outcome Outing is a movement — one where personal growth, meaningful
            connections, and curated travel blend into something unforgettable.
          </p>

          <p className="text-lg leading-relaxed font-jakarta mb-4">
            We&apos;re on a mission to launch 100+ such trips across India and
            beyond, led by mentors, loved by tribes, and driven by intent. This
            is just the beginning.
          </p>

          <p className="text-lg leading-relaxed font-jakarta mb-6">
            If you&apos;re ready to stop traveling on autopilot — and start
            exploring with meaning...
          </p>

          <div className="bg-gradient-to-r from-[#FFAE00]/10 to-[#FFAE00]/5 rounded-lg p-6 text-center">
            <p className="text-xl font-bricolage font-medium text-[#FFAE00] mb-2">
              Welcome to the Tribe. Your Outcome Awaits.
            </p>
          </div>
        </div>

        {/* Call to Action */}
        <div className="flex justify-center w-full pt-8">
          <Button
            variant="default"
            className="font-jakarta font-semibold text-white bg-[#FFAE00] hover:bg-[#FFAE00]/90 text-lg px-8 py-3 rounded-xl transition-all duration-200 flex items-center gap-2 group"
            onClick={handleClick}
          >
            Explore Experiences
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Page;
